<template>
  <loadingCube v-if="loading" />

  <div v-else class="pane">
    <ValidationObserver ref="form" slim>
      <form @submit.prevent="uploadFile" novalidate>
        <label>File:</label>
        <file_input
            @file-chosen="onFileSelected"
            :required="true"
            :selected_file="file"
            hidden_id="timesheet_file"
            file_error_name="Timesheet File"
            accepted_file_type=".xlsx,.xls,.csv"
        />

        <p v-if="fileError" class="text-danger" style="margin-top:0.5rem;">
          {{ fileError }}
        </p>

        <!-- Admin-only ignore validation -->
        <div
            v-if="apiScope === 'admin'"
            class="form-check"
            style="margin-top:10px;"
        >
          <input
              id="ignore_validation"
              :disabled="!isGod"
              type="checkbox"
              class="form-check-input"
              v-model="ignoreValidation"
          />
          <label for="ignore_validation" class="form-check-label">
            Ignore validation result
          </label>
        </div>
        <div v-else style="margin-top: 2.5em;"></div>

        <button
            class="btn orcaBtn"
            style="margin-top: 10px;"
            type="submit"
            :disabled="saving || !file"
        >
          <span v-if="saving" class="spinner"></span>
          <span v-if="saving">Uploading ...</span>
          <span v-else>Upload Timesheet File</span>
        </button>
      </form>
    </ValidationObserver>
  </div>
</template>

<style lang="scss" scoped>
.pane {
  padding: 1em;
  background-color: white;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 1em;
}


.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}


</style>

<script>
import file_input from 'partials/files/fileInput.vue';
import loadingCube from 'loaders/record_loader.vue';
import {isGod} from "./index";

export default {
  name: 'TimesheetFileUpload',

  components: {
    loadingCube,
    file_input,
  },

  props: {
    prefix: { required: true },
    company_id: { required: true },
    start_date: { required: true },
    end_date: { required: true },
    adminMode: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      saving: false,
      file: null,
      fileError: '',
      errorSig: '',
      ignoreValidation: false,
      importId: this.$route.params.importId
          ? Number(this.$route.params.importId)
          : null,
    };
  },

  computed: {
    apiScope() {
      return this.prefix === 'a' ? 'admin' : 'client';
    },
  },

  methods: {
    isGod,
    importBasePath() {
      return `${this.crmApiPath}r3/${this.apiScope}/timesheets/company/${this.company_id}/imports`;
    },

    onFileSelected(file) {
      this.file = file;
      this.clearFileError();
    },

    setFileError(msgOrArray) {
      const msgs = Array.isArray(msgOrArray) ? msgOrArray : [msgOrArray];
      const text = [...new Set(msgs.map(m => String(m).trim()))].join(' ');
      if (text === this.errorSig) return;
      this.errorSig = text;
      this.fileError = text;
    },

    clearFileError() {
      this.fileError = '';
      this.errorSig = '';
    },

    async uploadFile() {
      this.clearFileError();

      const ok = await this.$refs.form.validate();
      if (!ok) {
        this.formErrorsValidationScroll(this.$refs.form);
        return;
      }

      if (!this.file) {
        this.setFileError('Please choose a file.');
        return;
      }

      if (!/\.(csv|xls|xlsx)$/i.test(this.file.name || '')) {
        this.setFileError('The timesheet file must have a .csv, .xls or .xlsx extension.');
        return;
      }

      this.saving = true;

      try {
        const form = new FormData();
        form.append('company_id', this.company_id);
        form.append('submission_date', this.end_date);
        form.append('timesheet_file', this.file);

        if (this.adminMode && this.apiScope === 'admin' && this.ignoreValidation) {
          form.append('ignore_validation', '1');
        }

        const idem =
            crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;

        const { data } = await axios.post(this.importBasePath(), form, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Idempotency-Key': idem,
          },
        });

        if (!data?.id) {
          this.$snotify.error('Could not queue import');
          return;
        }

        this.importId = Number(data.id);

        await this.$router.replace({
          name: `${this.prefix}_timesheet_import_processing`,
          params: {
            company_id: this.company_id,
            importId: this.importId,
            start_date: this.start_date,
            end_date: this.end_date,
          },
        });
      } catch (e) {
        const r = e?.response;
        const errs = r?.data?.validation_errors || r?.data?.errors || {};
        if (errs.timesheet_file) {
          this.setFileError(errs.timesheet_file);
        } else {
          this.$snotify.error(r?.data?.message || 'Upload failed');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

